import { useId, useRef, useState, type KeyboardEvent, type ReactElement } from 'react'
import { cn } from '@/lib/utils'
import { IconChevronUpDown } from './icons'

export interface DropdownOption<T extends string> {
  value: T
  label: string
}

interface DropdownSelectProps<T extends string> {
  label: string
  value: T
  options: DropdownOption<T>[]
  onChange: (value: T) => void
  align?: 'left' | 'right'
}

function CheckIcon(): ReactElement {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 16 16"
      className="size-4 shrink-0"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M3 8.5 6.5 12 13 4.5" />
    </svg>
  )
}

export function DropdownSelect<T extends string>({
  label,
  value,
  options,
  onChange,
  align = 'right',
}: DropdownSelectProps<T>): ReactElement {
  const listId = useId()
  const rootRef = useRef<HTMLDivElement>(null)
  const [open, setOpen] = useState(false)

  const selectedIndex = Math.max(
    0,
    options.findIndex((option) => option.value === value),
  )
  const [activeIndex, setActiveIndex] = useState(selectedIndex)

  const current = options.find((option) => option.value === value)

  const openList = (): void => {
    setActiveIndex(selectedIndex)
    setOpen(true)
  }

  const select = (index: number): void => {
    const option = options[index]
    if (option) onChange(option.value)
    setOpen(false)
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>): void => {
    if (!open) {
      if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(event.key)) {
        event.preventDefault()
        openList()
      }
      return
    }

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault()
        setActiveIndex((index) => Math.min(index + 1, options.length - 1))
        break
      case 'ArrowUp':
        event.preventDefault()
        setActiveIndex((index) => Math.max(index - 1, 0))
        break
      case 'Home':
        event.preventDefault()
        setActiveIndex(0)
        break
      case 'End':
        event.preventDefault()
        setActiveIndex(options.length - 1)
        break
      case 'Enter':
      case ' ':
        event.preventDefault()
        select(activeIndex)
        break
      case 'Escape':
        event.preventDefault()
        setOpen(false)
        break
      case 'Tab':
        setOpen(false)
        break
    }
  }

  return (
    <div
      ref={rootRef}
      className="relative shrink-0"
      onBlur={(event) => {
        if (!rootRef.current?.contains(event.relatedTarget)) setOpen(false)
      }}
    >
      <button
        type="button"
        role="combobox"
        aria-label={label}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        aria-activedescendant={open ? `${listId}-${activeIndex}` : undefined}
        onClick={() => {
          if (open) setOpen(false)
          else openList()
        }}
        onKeyDown={handleKeyDown}
        className={cn(
          'hover:bg-btn-secondary-raised dark:hover:bg-panel-sidebar flex cursor-pointer items-center justify-center gap-2 rounded-xs py-1 pr-1.5 pl-4 transition-colors',
          open && 'bg-btn-secondary-raised dark:bg-panel-sidebar',
        )}
      >
        <span className="hub-label-md text-btn-text whitespace-nowrap">
          {current?.label ?? value}
        </span>
        <IconChevronUpDown className="text-btn-icon size-4 shrink-0" aria-hidden="true" />
      </button>

      {open && (
        <ul
          id={listId}
          role="listbox"
          aria-label={label}
          onMouseDown={(event) => {
            event.preventDefault()
          }}
          className={cn(
            'absolute top-full z-50 mt-2 flex min-w-48 flex-col gap-0.5 rounded-xl border border-[var(--popover-foreground)]/15 bg-[var(--popover)] p-1.5 shadow-[0_4px_16px_rgba(0,0,0,0.08)]',
            align === 'right' ? 'right-0' : 'left-0',
          )}
        >
          {options.map((option, index) => {
            const selected = option.value === value
            return (
              <li
                key={option.value}
                id={`${listId}-${index}`}
                role="option"
                aria-selected={selected}
                onClick={() => {
                  select(index)
                }}
                onMouseEnter={() => {
                  setActiveIndex(index)
                }}
                className={cn(
                  'hub-label-md flex cursor-pointer items-center justify-between gap-6 rounded-md px-3 py-2 text-[var(--popover-foreground)]',
                  index === activeIndex && 'bg-[var(--popover-foreground)]/8',
                )}
              >
                <span>{option.label}</span>
                {selected && <CheckIcon />}
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}
